import React, { useContext, useMemo } from 'react';
import useAllGroups from '../groups/useAllGroups';
import useAllSlimPosts from '../post/useAllSlimPosts';
import useAllTopics from '../topic/useAllTopics';
import useAllUsers from '../user/useAllUsers';

export const postStateData = [
  {
    id: 'published',
    name: 'Published',
  },
  {
    id: 'draft',
    name: 'Draft',
  },
  {
    id: 'archived',
    name: 'Archived',
  },
  {
    id: 'public',
    name: 'Public',
  },
  {
    id: 'verified',
    name: 'Verified',
  },
  {
    id: 'expired',
    name: 'Expired',
  },
] as const;

const postStateObjects = postStateData.map(
  (data) => ({ ...data, type: 'postState' } as const),
);

function useSearchObjectsValue() {
  const users = useAllUsers();
  const posts = useAllSlimPosts();
  const groups = useAllGroups();
  const topics = useAllTopics({ flatten: true });

  return useMemo(
    () => ({
      topic: {
        objects: topics,
      },
      user: {
        objects: users,
      },
      postState: {
        objects: postStateObjects,
      },
      post: {
        objects: posts,
      },
      group: {
        objects: groups,
      },
    }),
    [groups, posts, topics, users],
  );
}

const SearchObjectsContext = React.createContext<ReturnType<
  typeof useSearchObjectsValue
> | null>(null);

interface SearchObjectsProviderProps {
  children: React.ReactNode;
}

export const SearchObjectsProvider = ({
  children,
}: SearchObjectsProviderProps) => {
  const searchObjects = useSearchObjectsValue();

  return (
    <SearchObjectsContext.Provider value={searchObjects}>
      {children}
    </SearchObjectsContext.Provider>
  );
};

export function useSearchObjects() {
  const context = useContext(SearchObjectsContext);
  if (context == null)
    throw new Error(
      "Can't use search objects context. Wrap your component in <SearchObjectsProvider />",
    );
  return context;
}
